import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ParsedTaskFields {
  lane: "Purchase" | "Bring" | "To-Do";
  title: string;
  details: string;
  job_id?: string;
  assignee_id?: string;
  vendor_id?: string;
  qty?: number;
  units?: string;
  due_date?: string;
  priority?: "low" | "normal" | "high";
  needs_job_selection?: boolean;
}

export interface VoiceCaptureResult {
  transcript: string;
  parsedFields: ParsedTaskFields;
  logId: string;
}

export const useVoiceCapture = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const lastSpeechTimeRef = useRef<number>(0);
  const autoStopCallbackRef = useRef<(() => void) | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const shouldCheckSilenceRef = useRef<boolean>(false);

  const checkSilence = () => {
    if (!analyserRef.current || !shouldCheckSilenceRef.current) {
      return;
    }

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average volume
    const average = dataArray.reduce((a, b) => a + b) / bufferLength;
    const silenceThreshold = 10; // Adjust based on testing

    if (average > silenceThreshold) {
      // Speech detected
      lastSpeechTimeRef.current = Date.now();
    } else {
      // Check if 5 seconds of silence have passed
      const silenceDuration = Date.now() - lastSpeechTimeRef.current;
      if (silenceDuration >= 5000 && autoStopCallbackRef.current) {
        console.log("5 seconds of silence detected, auto-stopping recording");
        shouldCheckSilenceRef.current = false;
        autoStopCallbackRef.current();
        return;
      }
    }

    // Continue checking
    if (shouldCheckSilenceRef.current) {
      animationFrameRef.current = requestAnimationFrame(checkSilence);
    }
  };

  const startRecording = async (autoStopCallback?: () => void): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      // Set up audio analysis for silence detection
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingDuration(0);

      // Store auto-stop callback
      autoStopCallbackRef.current = autoStopCallback || null;
      lastSpeechTimeRef.current = Date.now();

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      // Start silence detection
      shouldCheckSilenceRef.current = true;
      checkSilence();

      return true;
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Could not access microphone. Please check permissions.");
      return false;
    }
  };

  const stopRecording = (): Promise<Blob> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(new Blob());
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        
        // Clean up
        shouldCheckSilenceRef.current = false;
        
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        if (mediaRecorderRef.current?.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
        }

        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }

        analyserRef.current = null;
        autoStopCallbackRef.current = null;

        setIsRecording(false);
        resolve(audioBlob);
      };

      mediaRecorderRef.current.stop();
    });
  };

  const processVoiceTask = async (
    audioBlob: Blob,
    contextJobId?: string
  ): Promise<VoiceCaptureResult> => {
    setIsProcessing(true);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(",")[1];
          resolve(base64);
        };
        reader.readAsDataURL(audioBlob);
      });

      const base64Audio = await base64Promise;

      // Call edge function to transcribe and parse
      const { data, error } = await supabase.functions.invoke("process-voice-task", {
        body: { 
          audio: base64Audio,
          contextJobId: contextJobId || null,
        },
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to process voice task");
      }

      console.log("Voice task processed:", data);

      return {
        transcript: data.transcript,
        parsedFields: data.parsedFields,
        logId: data.logId,
      };
    } catch (error: any) {
      console.error("Error processing voice task:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      shouldCheckSilenceRef.current = false;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      mediaRecorderRef.current.stop();
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      autoStopCallbackRef.current = null;
      setIsRecording(false);
      setRecordingDuration(0);
      audioChunksRef.current = [];
    }
  };

  return {
    isRecording,
    isProcessing,
    recordingDuration,
    startRecording,
    stopRecording,
    processVoiceTask,
    cancelRecording,
  };
};
